import { Fragment, useState, useRef, useCallback, useMemo } from "react";
import { Overlay, Tooltip, Button } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useGetMemItem } from "../../hooks/useMemoryDB";
import {
  useDataSaveHelp,
  useDataSaveNotes,
  useDataDeleteNote
} from "../../hooks/useData";
import usePermissions from "../../hooks/usePermissions";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faInfoCircle,
  faEdit,
  faStickyNote,
  faPlusSquare,
  faSpinner
} from "@fortawesome/free-solid-svg-icons";

import Editor from "react-simple-wysiwyg";
import moment from "moment";
import styles from "./help.module.scss";
import "./general.scss";

const defaultStyle = { marginLeft: "6px" };
const emptyArray = [];

const editorProps = {
  style: {
    resize: "vertical",
    textAlign: "left",
    height: "300px",
    backgroundColor: "black"
  }
};

export default function Help(props) {
  const { code, placement = "right", style = defaultStyle } = props;
  const [show, setShow] = useState(false);
  const [edit, setEdit] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const permissions = usePermissions();
  const saveNotes = useDataSaveNotes({ notify: true });
  const saveHelp = useDataSaveHelp({ notify: true });

  const target = useRef(null);

  const rawData = useGetMemItem("help", code)?.description || "";
  const rawNotes = useGetMemItem("notes", code)?.notes ?? emptyArray;
  const notesNum = useMemo(() => rawNotes?.length, [rawNotes]);

  const updateData = useCallback(
    (dx, cb) => {
      saveHelp({ code, dx }, (data) => {
        if (data.code === code) {
          // setEdit(false);
        }

        cb && cb(data);
      });
    },
    [code, saveHelp]
  );

  const upsertNotes = useCallback(
    (dx, cb) => {
      saveNotes({ code, dx }, (data) => {
        if (data.code === code) {
          //setShowNotes(false);
        }
        cb && cb(data);
      });
    },
    [code, saveNotes]
  );

  return (
    <div className={styles.container} style={style}>
      <div ref={target} onClick={() => setShow(!show)}>
        <FontAwesomeIcon icon={faInfoCircle} className={styles.icon} />
      </div>

      <Overlay
        target={target.current}
        show={show}
        placement={placement}
        rootClose={true}
        onHide={() => true}
      >
        {(props) => (
          <Tooltip id={code} {...props} className={styles.Tooltip}>
            {edit ? (
              <HelpEditor
                data={rawData}
                updateData={updateData}
                reset={() => setEdit(false)}
                close={() => setShowNotes(false)}
              />
            ) : showNotes ? (
              <HelpNotes
                close={() => setShowNotes(false)}
                reset={() => {
                  if (rawNotes.length === 0) {
                    setShowNotes(false);
                  }
                  setEdit(false);
                }}
                data={rawNotes}
                update={upsertNotes}
                code={code}
              />
            ) : (
              <Fragment>
                <div
                  dangerouslySetInnerHTML={{
                    __html:
                      rawData ||
                      `<p>Please enter a description for ${code}...</p>`
                  }}
                  className={styles.body}
                />
                <div className={styles.toolbar}>
                  {permissions.write?.includes("help") ? (
                    <FontAwesomeIcon
                      icon={faEdit}
                      className={styles.iconEdit}
                      onClick={(e) => setEdit(true)}
                    />
                  ) : null}

                  <span
                    className={styles.comments}
                    onClick={() => setShowNotes(true)}
                  >
                    <FontAwesomeIcon
                      icon={faStickyNote}
                      className={styles.icon}
                    />
                    <span className={styles.num}>{notesNum}</span>
                  </span>
                </div>
              </Fragment>
            )}
          </Tooltip>
        )}
      </Overlay>
    </div>
  );
}

function HelpEditor(props) {
  const { data, updateData, reset } = props;
  const [saving, setSaving] = useState(false);
  const [html, setHtml] = useState(data);

  const onSave = useCallback(() => {
    setSaving(true);
    updateData(html, () => setSaving(false));
  }, [html, updateData]);

  return (
    <div className={styles.editorContainer}>
      <Editor
        containerProps={editorProps}
        value={html}
        onChange={(e) => setHtml(e.target.value)}
      />
      <div className={styles.editorToolBtn}>
        <Button
          size="sm"
          variant="outline-light"
          className={styles.reset}
          onClick={reset}
        >
          Close
        </Button>
        <Button
          size="sm"
          variant="outline-success"
          className={styles.save}
          onClick={onSave}
        >
          {saving && <FontAwesomeIcon icon={faSpinner} pulse />} Save
        </Button>
      </div>
    </div>
  );
}

function HelpNotes(props) {
  const { data, update, close, code, reset } = props;
  const [edit, setEdit] = useState(data?.length === 0);
  const author = useSelector((state) => state.user.nickname);

  const addNew = useCallback(
    (body, cb) => {
      const notes = [{ date: new Date(), author, body }, ...data];
      update(notes, cb);
      //setEdit(false);
    },
    [author, data, update]
  );

  const updateNote = useCallback(
    (index, body, cb) => {
      const notes = [...data];
      notes[index] = { date: new Date(), author, body };
      update(notes, cb);
    },
    [author, data, update]
  );

  return (
    <div className={styles.notesContainer}>
      {edit ? (
        <HelpEditor
          updateData={addNew}
          reset={() => {
            reset();
            setEdit(false);
          }}
        />
      ) : (
        <Fragment>
          <div className={styles.box}>
            {data.length === 0 && (
              <div className={styles.empty}>
                <p>No notes found for this item.</p>
              </div>
            )}
            {data?.map((note, index) => (
              <Note
                key={`note-${index}`}
                index={index}
                data={note}
                code={code}
                update={updateNote}
              />
            ))}
          </div>
          <div className={styles.notesToolbar}>
            <div className={styles.close}>
              <Button
                size="sm"
                variant="outline-light"
                className={styles.btn}
                onClick={close}
              >
                Close Notes
              </Button>
            </div>
            <div className={styles.add}>
              <Button
                variant="outline-success"
                className={styles.btn}
                onClick={() => setEdit(true)}
                size="sm"
              >
                <FontAwesomeIcon icon={faPlusSquare} className={styles.icon} />
                Add Note
              </Button>
            </div>
          </div>
        </Fragment>
      )}
    </div>
  );
}

function Note(props) {
  const { index, data, update, code } = props;
  const [edit, setEdit] = useState(false);
  const permissions = usePermissions();
  const destroy = useDataDeleteNote({ notify: true });
  const theData = useMemo(
    () => moment(data.date).format("dddd, D MMM YYYY"),
    [data.date]
  );

  const handleDelete = useCallback(() => {
    destroy({ code, index });
  }, [code, destroy, index]);

  return (
    <Fragment>
      {edit ? (
        <div className={styles.noteEditingBox}>
          <HelpEditor
            updateData={(value, cb) => update(index, value, cb)}
            reset={() => setEdit(false)}
            data={data.body}
          />
        </div>
      ) : (
        <div className={styles.note}>
          <div className={styles.header}>
            <div className={styles.author}>{data.author}</div>
            <div className={styles.date}>{theData}</div>
            <div className={styles.spacer} />
            <div className={styles.delete} onClick={handleDelete}>
              &#x2715;
            </div>
          </div>
          <div
            className={styles.body}
            dangerouslySetInnerHTML={{ __html: data.body }}
          ></div>
          <div className={styles.toolbar}>
            {permissions.write?.includes("notes") ? (
              <FontAwesomeIcon
                icon={faEdit}
                className={styles.iconEdit}
                onClick={(e) => setEdit(true)}
              />
            ) : null}
          </div>
        </div>
      )}
    </Fragment>
  );
}
